/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable function-paren-newline */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  FilterAccordionHeader,
  Row,
  Col,
  Select,
  Checkbox,
  TextField,
  MultiSelection,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, FormSpy } from 'react-final-form';
import { useStripes } from '@folio/stripes/core';
import { useCatalogingFieldLabels } from '../../../../../helpers/catalogingFieldLabels';
import { useProductivityFieldLabels } from '../../../../../helpers/productivityFieldLabels';
import { required } from '../../../../../helpers/Validations';
import displayMethods from '../../../../../constants/DisplayMethods';

// Currency code → symbol map for common currencies
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', SAR: 'ر.س', AED: 'د.إ',
  KWD: 'د.ك', QAR: 'ر.ق', BHD: 'د.ب', OMR: 'ر.ع', JOD: 'د.أ',
  EGP: 'ج.م', MAD: 'د.م', TND: 'د.ت', JPY: '¥', CNY: '¥',
  INR: '₹', TRY: '₺', CAD: 'CA$', AUD: 'A$',
};

function ReportVisualization({ queryDimensions = [] }) {
  const [filterToggle, setFilterToggle] = useState(true);
  const intl = useIntl();
  const stripes = useStripes();
  const catalogingFieldLabels = useCatalogingFieldLabels();
  const productivityFieldLabels = useProductivityFieldLabels();

  // Build dimension options using translated field labels
  const dimensionOptions = useMemo(() => [
    { value: '', label: intl.formatMessage({ id: 'ui-reports.select.placeholder', defaultMessage: 'Select...' }) },
    ...queryDimensions.map(dim => {
      const dimNorm = dim.replace('.', '_');
      const label = catalogingFieldLabels[dim] || catalogingFieldLabels[dimNorm]
        || productivityFieldLabels[dim] || productivityFieldLabels[dimNorm]
        || dim.split('.').pop().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return { value: dim, label };
    }),
  ], [queryDimensions, catalogingFieldLabels, productivityFieldLabels, intl]);
  // Default currency symbol from tenant locale settings
  const tenantCurrencyCode   = stripes?.currency || 'USD';
  const tenantCurrencySymbol = CURRENCY_SYMBOLS[tenantCurrencyCode] || tenantCurrencyCode;

  // Convert displayMethods to options format with translations
  const displayMethodOptions = displayMethods.map(method => ({
    value: method.value,
    label: intl.formatMessage(method.translationKey)
  }));

  return (
    <Accordion
      id="save-new-report-visualization-accordion"
      label={
        <FormattedMessage
          id="ui-reports.saveNewReportForm.visualization.accordion.header"
          defaultMessage="Visualization Settings"
        />
      }
      onToggle={() => setFilterToggle(!filterToggle)}
      open={filterToggle}
      separator
      header={FilterAccordionHeader}
    >
      {/* Display Methods - MultiSelection */}
      <Row>
        <Col xs={12}>
          <Field
            name="visualization.displayMethods"
            component={MultiSelection}
            label={
              <FormattedMessage
                id="ui-reports.visualization.displayMethods"
                defaultMessage="Available Display Methods"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-reports.visualization.displayMethods.placeholder',
              defaultMessage: 'Select display methods',
            })}
            dataOptions={displayMethodOptions}
            formatter={({ option }) => option.label}
            itemToString={(item) => (item ? item.value : '')}
            required
            validate={required}
          />
        </Col>
      </Row>

      {/* Default Display Method - Dependent on selected methods */}
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          const selectedMethods = values.visualization?.displayMethods || [];

          return (
            <Row>
              <Col xs={12}>
                <Field
                  name="visualization.defaultDisplayMethod"
                  component={Select}
                  label={
                    <FormattedMessage
                      id="ui-reports.visualization.defaultDisplayMethod"
                      defaultMessage="Default Display Method"
                    />
                  }
                  placeholder={intl.formatMessage({
                    id: 'ui-reports.select.placeholder',
                    defaultMessage: 'Select...',
                  })}
                  dataOptions={selectedMethods}
                  disabled={selectedMethods.length === 0}
                  required={selectedMethods.length > 0}
                  validate={selectedMethods.length > 0 ? required : undefined}
                />
              </Col>
            </Row>
          );
        }}
      </FormSpy>

      {/* Table Options - Show only if Table is selected */}
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          const selectedMethods = values.visualization?.displayMethods || [];
          const hasTable = selectedMethods.some(
            (m) => (typeof m === 'object' ? m.value : m) === 'Table',
          );

          return (
            hasTable && (
              <>
                <Row>
                  <Col xs={12}>
                    <h4>
                      <FormattedMessage
                        id="ui-reports.visualization.tableOptions"
                        defaultMessage="Table Options"
                      />
                    </h4>
                  </Col>
                </Row>

                <Row>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.pagination"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.pagination"
                          defaultMessage="Pagination"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.sortable"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.sortable"
                          defaultMessage="Sortable"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.filterable"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.filterable"
                          defaultMessage="Filterable"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.exportable"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.exportable"
                          defaultMessage="Exportable"
                        />
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.showTotals"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.showTotals"
                          defaultMessage="Show Totals"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.showSubtotals"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.showSubtotals"
                          defaultMessage="Show Subtotals"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.pageSize"
                      component={TextField}
                      type="number"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.pageSize"
                          defaultMessage="Page Size"
                        />
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <Field
                      name="visualization.tableOptions.frozenColumns"
                      component={TextField}
                      type="number"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.frozenColumns"
                          defaultMessage="Frozen Columns"
                        />
                      }
                    />
                  </Col>
                </Row>
              </>
            )
          );
        }}
      </FormSpy>

      {/* Chart Options - Show only if any chart is selected */}
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          const selectedMethods = values.visualization?.displayMethods || [];
          const chartMethods = ['barChart', 'pieChart', 'lineChart', 'areaChart'];
          const hasChart = selectedMethods.some((m) => {
            const value = typeof m === 'object' ? m.value : m;
            return chartMethods.includes(value);
          });
          const hasAxes = selectedMethods.some((m) => {
            const value = typeof m === 'object' ? m.value : m;
            return ['barChart', 'lineChart', 'areaChart'].includes(value);
          });

          return (
            hasChart && (
              <>
                <Row>
                  <Col xs={12}>
                    <h4>
                      <FormattedMessage
                        id="ui-reports.visualization.chartOptions"
                        defaultMessage="Chart Options"
                      />
                    </h4>
                  </Col>
                </Row>

                {/* Colors */}
                <Row>
                  <Col xs={12}>
                    <Field
                      name="visualization.chartOptions.colorsInput"
                      component={TextField}
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.chartOptions.colors"
                          defaultMessage="Custom Colors (comma-separated hex, e.g. #FF0000, #00FF00)"
                        />
                      }
                      placeholder="#7DB3FF, #49457B, #FF7C78"
                      parse={(v) => v}
                      format={(v) =>
                        Array.isArray(v) ? v.join(', ') : (v || '')
                      }
                    />
                  </Col>
                </Row>

                {/* Legend */}
                <Row>
                  <Col xs={4}>
                    <Field
                      name="visualization.chartOptions.legend.show"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.chartOptions.legend.show"
                          defaultMessage="Show Legend"
                        />
                      }
                    />
                  </Col>
                  <Col xs={4}>
                    <Field
                      name="visualization.chartOptions.legend.position"
                      component={Select}
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.chartOptions.legend.position"
                          defaultMessage="Legend Position"
                        />
                      }
                      dataOptions={[
                        { value: 'bottom', label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.legend.bottom', defaultMessage: 'Bottom' }) },
                        { value: 'top',    label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.legend.top',    defaultMessage: 'Top' }) },
                        { value: 'left',   label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.legend.left',   defaultMessage: 'Left' }) },
                        { value: 'right',  label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.legend.right',  defaultMessage: 'Right' }) },
                      ]}
                    />
                  </Col>
                  <Col xs={4}>
                    <Field
                      name="visualization.chartOptions.animations"
                      component={Checkbox}
                      type="checkbox"
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.animations"
                          defaultMessage="Animations"
                        />
                      }
                    />
                  </Col>
                </Row>

                {/* Axes — only for bar/line/area */}
                {hasAxes && (
                  <>
                    <Row>
                      <Col xs={4}>
                        <Field
                          name="visualization.chartOptions.axes.xAxis.label"
                          component={TextField}
                          label={
                            <FormattedMessage
                              id="ui-reports.visualization.chartOptions.axes.xLabel"
                              defaultMessage="X-Axis Label"
                            />
                          }
                        />
                      </Col>
                      <Col xs={4}>
                        <Field
                          name="visualization.chartOptions.axes.yAxis.label"
                          component={TextField}
                          label={
                            <FormattedMessage
                              id="ui-reports.visualization.chartOptions.axes.yLabel"
                              defaultMessage="Y-Axis Label"
                            />
                          }
                        />
                      </Col>
                      <Col xs={4}>
                        <Field
                          name="visualization.chartOptions.axes.yAxis.scale"
                          component={Select}
                          label={
                            <FormattedMessage
                              id="ui-reports.visualization.chartOptions.axes.yScale"
                              defaultMessage="Y-Axis Scale"
                            />
                          }
                          dataOptions={[
                            { value: 'linear',      label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.axes.linear',      defaultMessage: 'Linear' }) },
                            { value: 'logarithmic', label: intl.formatMessage({ id: 'ui-reports.visualization.chartOptions.axes.logarithmic', defaultMessage: 'Logarithmic' }) },
                          ]}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={4}>
                        <Field
                          name="visualization.chartOptions.axes.xAxis.showGrid"
                          component={Checkbox}
                          type="checkbox"
                          label={
                            <FormattedMessage
                              id="ui-reports.visualization.chartOptions.axes.showGrid"
                              defaultMessage="Show Grid Lines"
                            />
                          }
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )
          );
        }}
      </FormSpy>
      {/* Formatting Options */}
      <Row>
        <Col xs={12}>
          <h4>
            <FormattedMessage
              id="ui-reports.visualization.formatting"
              defaultMessage="Data Formatting"
            />
          </h4>
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Field
            name="visualization.formatting.numberFormat"
            component={Select}
            label={
              <FormattedMessage
                id="ui-reports.visualization.formatting.numberFormat"
                defaultMessage="Number Format"
              />
            }
            dataOptions={[
              { value: '',        label: intl.formatMessage({ id: 'ui-reports.visualization.formatting.none', defaultMessage: 'None' }) },
              { value: '0,0',     label: '1,234' },
              { value: '0,0.00',  label: '1,234.56' },
              { value: '0.00',    label: '1234.56' },
              { value: '0,0.000', label: '1,234.567' },
              { value: '0%',      label: '12%' },
              { value: '0.00%',   label: '12.34%' },
            ]}
          />
        </Col>
        <Col xs={4}>
          <Field
            name="visualization.formatting.dateFormat"
            component={Select}
            label={
              <FormattedMessage
                id="ui-reports.visualization.formatting.dateFormat"
                defaultMessage="Date Format"
              />
            }
            dataOptions={[
              { value: '',             label: intl.formatMessage({ id: 'ui-reports.visualization.formatting.none', defaultMessage: 'None' }) },
              { value: 'DD/MM/YYYY',   label: 'DD/MM/YYYY — 31/12/2024' },
              { value: 'MM/DD/YYYY',   label: 'MM/DD/YYYY — 12/31/2024' },
              { value: 'YYYY-MM-DD',   label: 'YYYY-MM-DD — 2024-12-31' },
              { value: 'DD MMM YYYY',  label: 'DD MMM YYYY — 31 Dec 2024' },
              { value: 'DD-MM-YYYY',   label: 'DD-MM-YYYY — 31-12-2024' },
              { value: 'DD/MM/YYYY HH:mm', label: 'DD/MM/YYYY HH:mm — 31/12/2024 09:30' },
            ]}
          />
        </Col>
        <Col xs={4}>
          <Field
            name="visualization.formatting.currencyFormat.symbol"
            component={TextField}
            label={
              <FormattedMessage
                id="ui-reports.visualization.formatting.currencySymbol"
                defaultMessage="Currency Symbol ({tenant})"
                values={{ tenant: `${tenantCurrencyCode} ${tenantCurrencySymbol}` }}
              />
            }
            placeholder={tenantCurrencySymbol}
          />
        </Col>
      </Row>

      {/* Grouped View / Pivot Table dimension selectors */}
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          const selectedMethods = values.visualization?.displayMethods || [];
          const hasGrouped = selectedMethods.some(m => (typeof m === 'object' ? m.value : m) === 'groupedView');
          const hasPivot   = selectedMethods.some(m => (typeof m === 'object' ? m.value : m) === 'pivotTable');

          // Collect available dimensions from query (stored in form as string keys)
          // We derive them from the current query dimensions if available,
          // otherwise show a free-text field
          if (!hasGrouped && !hasPivot) return null;

          return (
            <>
              {hasGrouped && (
                <Row>
                  <Col xs={12}>
                    <Field
                      name="visualization.groupByDimension"
                      component={Select}
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.groupByDimension"
                          defaultMessage="Grouped View — Group By Dimension"
                        />
                      }
                      dataOptions={dimensionOptions}
                    />
                  </Col>
                </Row>
              )}
              {hasPivot && (
                <Row>
                  <Col xs={6}>
                    <Field
                      name="visualization.pivotRowDimension"
                      component={Select}
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.pivotRowDimension"
                          defaultMessage="Pivot Table — Row Dimension"
                        />
                      }
                      dataOptions={dimensionOptions}
                    />
                  </Col>
                  <Col xs={6}>
                    <Field
                      name="visualization.pivotColDimension"
                      component={Select}
                      label={
                        <FormattedMessage
                          id="ui-reports.visualization.pivotColDimension"
                          defaultMessage="Pivot Table — Column Dimension"
                        />
                      }
                      dataOptions={dimensionOptions}
                    />
                  </Col>
                </Row>
              )}
            </>
          );
        }}
      </FormSpy>

    </Accordion>
  );
}

ReportVisualization.propTypes = {
  queryDimensions: PropTypes.arrayOf(PropTypes.string),
};

export default ReportVisualization;
